import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormattedMessage } from '../../util/reactIntl';
import { findOptionsForSelectFilter } from '../../util/search';
import { propTypes } from '../../util/types';
import config from '../../config';
import { Button, FieldCheckboxGroup, Form, UnsavedInfoPrompt } from '../../components';

import css from './EditListingLanguagesForm.css';
import * as validators from '../../util/validators';

const EditListingLanguagesFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        disabled,
        ready,
        rootClassName,
        className,
        name,
        handleSubmit,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        filterConfig,
        intl,
        dirty,
      } = formRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = disabled || submitInProgress;

      const { updateListingError, showListingsError } = fetchErrors || {};
      const errorMessage = updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingLanguagesForm.updateFailed" />
        </p>
      ) : null;

      const errorMessageShowListing = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingLanguagesForm.showListingFailed" />
        </p>
      ) : null;

      const languageRequiredMessage = intl.formatMessage({
        id: 'EditListingLanguagesForm.languageRequired',
      });
      const languageRequired = validators.nonEmptyArray(languageRequiredMessage);

      const options = findOptionsForSelectFilter('languages', filterConfig);

      const mainOptions = options.filter(
        x => x.key === 'finnish' || x.key === 'swedish' || x.key === 'english'
      );

      const restOfTheOptions = options.filter(x => !mainOptions.includes(x));

      const compare = (a, b) => {
        const { intl } = props;
        if (
          intl.formatMessage({ id: a.label.props.id }) <
          intl.formatMessage({ id: b.label.props.id })
        ) {
          return -1;
        }
        if (
          intl.formatMessage({ id: a.label.props.id }) >
          intl.formatMessage({ id: b.label.props.id })
        ) {
          return 1;
        }
        return 0;
      };
      const restOfTheOptionsSorted = restOfTheOptions.sort(compare);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {errorMessage}
          {errorMessageShowListing}

          <FieldCheckboxGroup
            className={css.features}
            id={name}
            name={name}
            options={mainOptions}
            isLanguageForm={true}
            validate={languageRequired}
          />

          <FieldCheckboxGroup
            className={css.features}
            id={name}
            name={name}
            options={restOfTheOptionsSorted}
            validate={languageRequired}
            twoColumns={true}
          />

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
          <UnsavedInfoPrompt when={dirty} />
        </Form>
      );
    }}
  />
);

EditListingLanguagesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  fetchErrors: null,
  filterConfig: config.custom.filters,
};

EditListingLanguagesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  name: string.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  filterConfig: propTypes.filterConfig,
};

const EditListingLanguagesForm = EditListingLanguagesFormComponent;

export default EditListingLanguagesForm;
